
import { createBrowserClient } from '@supabase/ssr';
import type { GameState, MatchRecord, Organization, Season, Player } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// In production, VITE_COOKIE_DOMAIN=.sideoutiq.com shares the auth cookie with
// the landing site (sideoutiq.com) so it can detect logged-in users and redirect.
// Leave unset in local dev (localhost doesn't match .sideoutiq.com).
const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN as string | undefined;

export const supabase = createBrowserClient(supabaseUrl, supabaseKey, {
  cookieOptions: cookieDomain ? { domain: cookieDomain } : undefined,
  auth: {
    // Default is 10 s — raise to 30 s to reduce spurious lock-timeout errors
    // when multiple tabs compete for the auth token lock.
    lockAcquireTimeout: 30000,
    // Explicitly enable URL session detection — required for OAuth redirects
    // in a Vite SPA where there is no server-side /auth/callback route.
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

/**
 * Runs a Supabase operation. If the session JWT is expired, refreshes it and
 * retries once. Prevents "JWT expired" failures after the tab has been idle.
 */
export async function withAutoRefresh<T extends { data: any; error: any }>(
  operation: () => PromiseLike<T>
): Promise<T> {
  const result = await operation();
  if (result.error?.message?.includes('JWT expired')) {
    await supabase.auth.refreshSession();
    return operation() as Promise<T>;
  }
  return result;
}

// Helpers to map DB snake_case to App camelCase

export const mapProfileFromDB = (p: any) => ({
  id: p.id,
  email: p.email,
  fullName: p.full_name,
  appSettings: p.app_settings ?? undefined,
});

export const mapOrganizationFromDB = (o: any, role?: string, isAdmin?: boolean) => ({
  id: o.id,
  name: o.name,
  joinCode: o.join_code,
  role: role || 'member',
  isAdmin: isAdmin || false,
  // undefined when column not in query (backwards compat); null = pending; string = approved
  approvedAt: o.approved_at !== undefined ? o.approved_at : undefined,
  isBetaTester: o.is_beta_tester ?? false,
  betaApprovalDate: o.beta_approval_date ?? null,
  betaExpiresAt: o.beta_expires_at ?? null,
  matchCreditsUsed: o.match_credits_used ?? 0,
  stripeSubscriptionStatus: o.stripe_subscription_status ?? 'none',
  unitPreference: (o.unit_preference as 'imperial' | 'metric' | undefined) ?? 'imperial',
});

export const mapSeasonFromDB = (s: any): Season => ({
  id: s.id,
  organizationId: s.organization_id,
  name: s.name,
  startDate: s.start_date ?? null,
  endDate: s.end_date ?? null,
  isActive: s.is_active,
  rosterSnapshot: s.roster_snapshot ?? null,
  createdAt: s.created_at,
});

export const mapTeamFromDB = (t: any) => ({
  id: t.id,
  organizationId: t.organization_id,
  name: t.name,
  isDefault: t.is_default || false,
  isFavorite: !!(t.is_favorite || t.is_default),
  ageGroup: t.age_group,
  type: t.type || 'club',
  eligibilityRequirements: t.eligibility_requirements || ['Physical', 'Waiver', 'Emergency Info', 'Insurance'],
  coachNotes: t.coach_notes || [
    { id: '1', title: 'Housekeeping', content: '' },
    { id: '2', title: 'Other', content: '' }
  ],
  isArchived: t.is_archived || false,
});

export const mapPlayerFromDB = (p: any) => ({
  id: p.id,
  organizationId: p.organization_id,
  teamId: p.team_id || 'unassigned',
  name: p.name,
  jerseyNumber: p.jersey_number,
  position: p.position,
  photoUrl: p.photo_url,
  email: p.email,
  phone: p.phone,
  parent1Name: p.parent1_name,
  parent1Contact: p.parent1_contact,
  parent2Name: p.parent2_name,
  parent2Contact: p.parent2_contact,
  goals: p.goals,
  notes: p.notes,
  rank: p.rank,
  // New Fields
  gradYear: p.grad_year,
  heightCm: p.height_cm ?? null,  // Canonical integer (cm)
  weightKg: p.weight_kg ?? null,  // Canonical decimal (kg)
  height: p.height,               // Legacy text — kept for migration fallback
  weight: p.weight,               // Legacy text — kept for migration fallback
  age: p.age,
  handedness: p.handedness,
  primaryServeType: p.primary_serve_type || undefined,
  secondaryPosition: p.secondary_position || undefined,
  eligibility: p.eligibility || {}
});

export const mapEventFromDB = (e: any) => ({
  id: e.id,
  organizationId: e.organization_id,
  type: e.type,
  title: e.title,
  date: e.date,
  time: e.time,
  endTime: e.end_time || undefined,
  location: e.location,
  opponent: e.opponent,
  teamId: e.team_id || undefined,
  tournamentId: e.tournament_id || undefined,
  tournamentName: e.tournament_name || undefined,
  stats: e.stats || [],
});

export const mapLineupFromDB = (l: any) => ({
  id: l.id,
  organizationId: l.organization_id,
  teamId: l.team_id,
  name: l.name,
  positions: l.positions, // JSONB
  liberoAssignments: Array.isArray(l.libero_assignments) ? l.libero_assignments : [],
  rotationalSubstitutions: l.rotational_substitutions || undefined,
  system: (l.system as '5-1' | '6-2') || '5-1',
  dualRoleSetterIds: Array.isArray(l.dual_role_setter_ids) ? l.dual_role_setter_ids : [],
  isDefault: l.is_default,
  isFavorite: l.is_favorite || false,
  createdAt: l.created_at
});

// Member Management

export const getPendingApprovalCount = async (orgId: string): Promise<number> => {
  if (orgId === 'dev-org-id') return 1; // show badge in dev mode
  const { count, error } = await supabase
    .from('organization_members')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('status', 'pending');
  if (error) return 0;
  return count ?? 0;
};

export const getOrgMembers = async (orgId: string) => {
  // Try with all new columns; gracefully fall back to legacy columns if migration hasn't run yet
  let members: any[] | null = null;
  let hasNewCols = true;

  const { data: newData, error: newError } = await supabase
    .from('organization_members')
    .select('id, user_id, role, is_admin, status, invited_name, invited_email, requested_role')
    .eq('organization_id', orgId);

  if (newError) {
    console.warn('getOrgMembers: new columns not found, using legacy query', newError.message);
    hasNewCols = false;
    const { data: legacyData, error: legacyError } = await supabase
      .from('organization_members')
      .select('id, user_id, role')
      .eq('organization_id', orgId);
    if (legacyError) throw legacyError;
    members = legacyData;
  } else {
    members = newData;
  }

  if (!members || members.length === 0) return [];

  // Two-step query: get profiles separately (user_id → auth.users, not directly to profiles table)
  const userIds = members.filter((m: any) => m.user_id).map((m: any) => m.user_id);
  let profilesMap: Record<string, { email: string; full_name: string }> = {};

  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in('id', userIds);
    if (profiles) {
      profiles.forEach((p: any) => { profilesMap[p.id] = { email: p.email, full_name: p.full_name }; });
    }
  }

  // Normalize legacy 'assistant_coach' → 'assistant' so the role dropdown always matches a valid option
  const normalizeRole = (role: string) => role === 'assistant_coach' ? 'assistant' : role;

  return members.map((m: any) => ({
    memberId: m.id,
    userId: m.user_id || null,
    role: normalizeRole(m.role),
    isAdmin: hasNewCols ? (m.is_admin || false) : false,
    status: hasNewCols ? ((m.status || 'active') as 'active' | 'pending' | 'invited') : 'active' as const,
    email: (hasNewCols ? m.invited_email : null) || profilesMap[m.user_id]?.email || '',
    fullName: (hasNewCols ? m.invited_name : null) || profilesMap[m.user_id]?.full_name || 'Unknown',
    requestedRole: hasNewCols ? (m.requested_role || undefined) : undefined,
  }));
};

export const updateMemberRole = async (memberId: string, newRole: string) => {
  const { error } = await supabase
    .from('organization_members')
    .update({ role: newRole })
    .eq('id', memberId);
  if (error) throw error;
};

export const updateMemberAdminStatus = async (memberId: string, isAdmin: boolean) => {
  const { error } = await supabase
    .from('organization_members')
    .update({ is_admin: isAdmin })
    .eq('id', memberId);
  if (error) throw error;
};

export const removeMember = async (memberId: string) => {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', memberId);
  if (error) throw error;
};

const BETA_MEMBER_CAP = 20;

export const getOrgMemberCount = async (orgId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('organization_members')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .in('status', ['active', 'invited']);
  if (error) throw error;
  return count ?? 0;
};

export const sendOrgInvite = async (
  orgId: string, name: string, email: string, role: string, isAdmin: boolean
) => {
  const currentCount = await getOrgMemberCount(orgId);
  if (currentCount >= BETA_MEMBER_CAP) {
    throw new Error(`Your organization has reached the ${BETA_MEMBER_CAP}-member limit for the beta period. Contact us to increase your limit.`);
  }

  const { data, error } = await supabase
    .from('organization_members')
    .insert([{
      organization_id: orgId,
      user_id: null,
      role,
      is_admin: isAdmin,
      status: 'invited',
      invited_name: name,
      invited_email: email,
    }])
    .select()
    .single();
  if (error) {
    // Only show the migration hint for actual "undefined column" errors (code 42703)
    if ((error as any).code === '42703') {
      throw new Error(
        'Database migration required: the invite columns (status, invited_name, invited_email, is_admin) don\'t exist yet. ' +
        'Please run the SQL migration in your Supabase dashboard.'
      );
    }
    console.error('sendOrgInvite error:', error);
    throw error;
  }
  return data;
};

export const approveRequest = async (memberId: string, role: string, orgId?: string) => {
  if (orgId) {
    const currentCount = await getOrgMemberCount(orgId);
    if (currentCount >= BETA_MEMBER_CAP) {
      throw new Error(`Your organization has reached the ${BETA_MEMBER_CAP}-member limit for the beta period.`);
    }
  }
  const { error } = await supabase
    .from('organization_members')
    .update({ status: 'active', role, requested_role: null })
    .eq('id', memberId);
  if (error) throw error;
};

export const rejectRequest = async (memberId: string) => {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', memberId);
  if (error) throw error;
};

export const getPreferredTeamId = async (userId: string, orgId: string): Promise<string | null> => {
  const { data } = await withAutoRefresh(() =>
    supabase.from('organization_members')
      .select('preferred_team_id')
      .eq('user_id', userId)
      .eq('organization_id', orgId)
      .maybeSingle()
  );
  return (data as any)?.preferred_team_id ?? null;
};

export const setPreferredTeamId = async (userId: string, orgId: string, teamId: string): Promise<void> => {
  const { error } = await withAutoRefresh(() =>
    supabase.from('organization_members')
      .update({ preferred_team_id: teamId })
      .eq('user_id', userId)
      .eq('organization_id', orgId)
  );
  if (error) throw error;
};

export const updateUserProfile = async (userId: string, fullName: string) => {
  const { error } = await supabase
    .from('profiles')
    .update({ full_name: fullName, updated_at: new Date().toISOString() })
    .eq('id', userId);
  if (error) throw error;
};

export const saveUserSettings = async (userId: string, settings: Record<string, any>) => {
  const { error } = await withAutoRefresh(() =>
    supabase
      .from('profiles')
      .update({ app_settings: settings, updated_at: new Date().toISOString() })
      .eq('id', userId)
  );
  if (error) console.error('Failed to save settings to DB:', error);
};

export const joinOrgWithRole = async (
  orgId: string, userId: string, role: string
) => {
  const coachRoles = ['head_coach', 'assistant_coach', 'coach'];
  const needsApproval = coachRoles.includes(role);
  const status = needsApproval ? 'pending' : 'active';
  const storedRole = needsApproval ? 'other' : role;

  // Pending members don't consume a slot until approved
  if (!needsApproval) {
    const currentCount = await getOrgMemberCount(orgId);
    if (currentCount >= BETA_MEMBER_CAP) {
      throw new Error(`This organization has reached the ${BETA_MEMBER_CAP}-member limit for the beta period.`);
    }
  }

  const { error } = await supabase
    .from('organization_members')
    .insert([{
      organization_id: orgId,
      user_id: userId,
      role: storedRole,
      is_admin: false,
      status,
      requested_role: needsApproval ? role : null,
    }]);
  if (error) throw error;
  return { status, role: storedRole, needsApproval };
};

// ─── Match Records ───────────────────────────────────────────────────────────

export const mapMatchRecordFromDB = (r: any): MatchRecord => ({
  id: r.id,
  eventId: r.event_id,
  organizationId: r.organization_id,
  submittedBy: r.submitted_by || null,
  submittedByName: r.submitted_by_name || 'Unknown',
  submittedAt: r.submitted_at,
  gameState: r.game_state as GameState,
  isRecord: r.is_record,
  teamId: r.team_id || undefined,
  isCreditConsumed: r.is_credit_consumed ?? false,
});

export const getMatchRecords = async (orgId: string): Promise<MatchRecord[]> => {
  const { data, error } = await supabase
    .from('match_records')
    .select('*')
    .eq('organization_id', orgId)
    .order('submitted_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapMatchRecordFromDB);
};

export const submitMatchRecord = async (
  orgId: string,
  eventId: string,
  userId: string,
  userName: string,
  gameState: GameState,
  teamId?: string,
): Promise<MatchRecord> => {
  // Proactively refresh the session so JWT-expired errors can't silently wipe
  // a match. getSession() auto-refreshes when the token is expired or near expiry.
  await supabase.auth.getSession();

  // Check if any submission exists for this event (to determine is_record)
  const { count } = await supabase
    .from('match_records')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('event_id', eventId);
  const isFirst = (count ?? 0) === 0;

  // Check if the current user already has a submission (to preserve is_record)
  const { data: existing } = await supabase
    .from('match_records')
    .select('id, is_record')
    .eq('organization_id', orgId)
    .eq('event_id', eventId)
    .eq('submitted_by', userId)
    .maybeSingle();

  let isRecord = isFirst;
  if (existing) isRecord = existing.is_record; // preserve existing record status on re-submit

  const payload = {
    organization_id: orgId,
    event_id: eventId,
    submitted_by: userId,
    submitted_by_name: userName,
    submitted_at: new Date().toISOString(),
    game_state: gameState,
    is_record: isRecord,
    team_id: teamId || null,
  };

  // Preserve existing credit-consumed flag on re-submits
  const existingCreditConsumed = existing
    ? (await supabase.from('match_records').select('is_credit_consumed').eq('id', existing.id).single()).data?.is_credit_consumed ?? false
    : false;

  // New first submission: consume a credit
  const shouldConsumeCredit = isFirst && !existingCreditConsumed;
  if (shouldConsumeCredit) {
    (payload as any).is_credit_consumed = true;
  } else if (existingCreditConsumed) {
    (payload as any).is_credit_consumed = true;
  }

  const { data, error } = existing
    ? await supabase.from('match_records').update(payload).eq('id', existing.id).select().single()
    : await supabase.from('match_records').insert([payload]).select().single();

  if (error) throw error;
  return mapMatchRecordFromDB(data);
};

// Always inserts a brand-new match_record — never updates an existing one.
// Use this for imports/manual entries so they never overwrite live-tracking records.
export const insertMatchRecord = async (
  orgId: string,
  eventId: string,
  userId: string,
  userName: string,
  gameState: GameState,
  teamId?: string,
): Promise<MatchRecord> => {
  await supabase.auth.getSession();

  // Only the very first record for an event is marked is_record=true
  const { count } = await supabase
    .from('match_records')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('event_id', eventId);
  const isFirst = (count ?? 0) === 0;

  // Check if this event already has a credit-consumed record (free merge)
  const { data: existingCredited } = await supabase
    .from('match_records')
    .select('id')
    .eq('organization_id', orgId)
    .eq('event_id', eventId)
    .eq('is_credit_consumed', true)
    .maybeSingle();

  const payload = {
    organization_id: orgId,
    event_id: eventId,
    submitted_by: userId,
    submitted_by_name: userName,
    submitted_at: new Date().toISOString(),
    game_state: gameState,
    is_record: isFirst,
    is_credit_consumed: isFirst && !existingCredited, // consume credit on first import; free merge if already unlocked
    team_id: teamId || null,
  };

  const { data, error } = await supabase
    .from('match_records')
    .insert([payload])
    .select()
    .single();

  if (error) throw error;
  return mapMatchRecordFromDB(data);
};

export const setMatchOfRecord = async (
  matchRecordId: string,
  eventId: string,
): Promise<void> => {
  // Clear record flag on all submissions for this event, then set it on the target
  const { error: clearError } = await supabase
    .from('match_records')
    .update({ is_record: false })
    .eq('event_id', eventId);
  if (clearError) throw clearError;

  const { error: setError } = await supabase
    .from('match_records')
    .update({ is_record: true })
    .eq('id', matchRecordId);
  if (setError) throw setError;
};

// ─── Billing / Credit Gatekeeper ─────────────────────────────────────────────

export const TRIAL_CREDIT_LIMIT = 3;

export type BillingStatus =
  | { status: 'active_subscription' }
  | { status: 'active_beta'; expiresAt: Date }
  | { status: 'trial'; creditsUsed: number }
  | { status: 'expired_trial'; creditsUsed: number }
  | { status: 'expired_beta'; creditsUsed: number };

/**
 * Derive the current billing state for an org from already-loaded data.
 * No extra DB call required — pass in the org and the already-loaded matchRecords.
 */
export const getBillingStatus = (
  org: Organization,
  matchRecords: MatchRecord[],
): BillingStatus => {
  // Step A: active Stripe subscription
  if (org.stripeSubscriptionStatus === 'active' || org.stripeSubscriptionStatus === 'trialing') {
    return { status: 'active_subscription' };
  }

  // Step B: beta tester check
  if (org.isBetaTester && org.betaApprovalDate) {
    const approvalDate = new Date(org.betaApprovalDate);
    const expiresAt = new Date(approvalDate.getTime() + 365 * 24 * 60 * 60 * 1000);
    if (new Date() < expiresAt) {
      return { status: 'active_beta', expiresAt };
    }
    // Beta expired — fall through to credit check
    const creditsUsed = matchRecords.filter(r => r.isCreditConsumed).length;
    return { status: 'expired_beta', creditsUsed };
  }

  // Step C: standard trial credits
  const creditsUsed = matchRecords.filter(r => r.isCreditConsumed).length;
  if (creditsUsed < TRIAL_CREDIT_LIMIT) {
    return { status: 'trial', creditsUsed };
  }
  return { status: 'expired_trial', creditsUsed };
};

/**
 * Check whether a new match action is allowed for this org.
 * Pass eventId when adding data to an existing match (free merge check).
 * Omit eventId when creating a brand-new match.
 */
export const checkMatchAccess = async (
  org: Organization,
  matchRecords: MatchRecord[],
  eventId?: string,
): Promise<{ allowed: boolean; reason?: string }> => {
  const billing = getBillingStatus(org, matchRecords);

  if (billing.status === 'active_subscription' || billing.status === 'active_beta') {
    return { allowed: true };
  }

  // If adding to an existing match — check if credit already consumed (free merge)
  if (eventId) {
    const alreadyConsumed = matchRecords.some(
      r => r.eventId === eventId && r.isCreditConsumed,
    );
    if (alreadyConsumed) return { allowed: true };
  }

  // New match: check credit count
  if (billing.status === 'trial' && billing.creditsUsed < TRIAL_CREDIT_LIMIT) {
    return { allowed: true };
  }

  return {
    allowed: false,
    reason: 'trial_exhausted',
  };
};

/**
 * Consume a match credit for an event (set is_credit_consumed = true on the record row).
 * Call this after successfully inserting the match record.
 */
export const consumeMatchCredit = async (matchRecordId: string): Promise<void> => {
  const { error } = await supabase
    .from('match_records')
    .update({ is_credit_consumed: true })
    .eq('id', matchRecordId);
  if (error) throw error;
};

// ─── Practice Plans ───────────────────────────────────────────────────────────

export const getPracticePlan = async (orgId: string, date: string): Promise<any[] | null> => {
  const { data, error } = await withAutoRefresh(() =>
    supabase
      .from('practice_plans')
      .select('blocks')
      .eq('organization_id', orgId)
      .eq('plan_date', date)
      .maybeSingle()
  );
  if (error || !data) return null;
  return data.blocks as any[];
};

export const upsertPracticePlan = async (
  orgId: string, date: string, blocks: any[]
): Promise<void> => {
  await withAutoRefresh(() =>
    supabase
      .from('practice_plans')
      .upsert(
        { organization_id: orgId, plan_date: date, blocks, updated_at: new Date().toISOString() },
        { onConflict: 'organization_id,plan_date' }
      )
  );
};

// ─── Practice Templates & Section Templates ───────────────────────────────────

export interface PracticeTemplateRow {
  id: string;
  name: string;
  template_type: 'full' | 'section';
  data: any[];
  is_default: boolean;
}

export const getTeamPracticeTemplates = async (teamId: string): Promise<PracticeTemplateRow[]> => {
  const { data, error } = await withAutoRefresh(() =>
    supabase
      .from('practice_templates')
      .select('id, name, template_type, data, is_default')
      .eq('team_id', teamId)
      .order('created_at', { ascending: true })
  );
  if (error || !data) return [];
  return data as PracticeTemplateRow[];
};

export const createTeamPracticeTemplate = async (
  teamId: string, orgId: string, name: string,
  type: 'full' | 'section', data: any[], isDefault = false
): Promise<string | null> => {
  const { data: row, error } = await withAutoRefresh(() =>
    supabase
      .from('practice_templates')
      .insert({ team_id: teamId, organization_id: orgId, name, template_type: type, data, is_default: isDefault })
      .select('id')
      .single()
  );
  if (error || !row) return null;
  return row.id as string;
};

export const updateTeamPracticeTemplate = async (
  id: string, updates: { name?: string; data?: any[]; is_default?: boolean }
): Promise<void> => {
  await withAutoRefresh(() =>
    supabase.from('practice_templates').update(updates).eq('id', id)
  );
};

export const deleteTeamPracticeTemplate = async (id: string): Promise<void> => {
  await withAutoRefresh(() =>
    supabase.from('practice_templates').delete().eq('id', id)
  );
};

export const clearDefaultPracticeTemplates = async (teamId: string): Promise<void> => {
  await withAutoRefresh(() =>
    supabase
      .from('practice_templates')
      .update({ is_default: false })
      .eq('team_id', teamId)
      .eq('is_default', true)
      .eq('template_type', 'full')
  );
};

// ─── Team Favorite Drills ─────────────────────────────────────────────────────

export const getTeamFavoriteDrills = async (teamId: string): Promise<any[]> => {
  const { data, error } = await withAutoRefresh(() =>
    supabase
      .from('team_favorite_drills')
      .select('drills')
      .eq('team_id', teamId)
      .maybeSingle()
  );
  if (error || !data) return [];
  return data.drills as any[];
};

export const upsertTeamFavoriteDrills = async (
  teamId: string, orgId: string, drills: any[]
): Promise<void> => {
  await withAutoRefresh(() =>
    supabase
      .from('team_favorite_drills')
      .upsert(
        { team_id: teamId, organization_id: orgId, drills, updated_at: new Date().toISOString() },
        { onConflict: 'team_id' }
      )
  );
};

